// src/Components/videochat/useZoomPan.js
//
// Zoom del video de la otra persona, como en la app iOS:
//  - por defecto cuadro completo (contain), zoom 1;
//  - pellizco (dos punteros) acerca hacia donde están los dedos, hasta el doble
//    de lo que hace falta para llenar la pantalla y al menos 3×;
//  - con zoom, un dedo arrastra el video, con inercia al soltar, sin pasarse de
//    las orillas;
//  - pasarse de los límites cede con resistencia y al soltar vuelve;
//  - al soltar, cerca de 1× vuelve a 1× y cerca de «llenar la pantalla» se
//    acomoda justo ahí.
// Todo se aplica directo al DOM (transform) para no renderizar en cada mover.
import { useCallback, useEffect, useRef } from 'react'

const RESISTANCE = 0.55
const SNAP_TO_FIT = 1.08
const SNAP_TO_FILL = 0.06
const MIN_MAX_ZOOM = 3

// Resistencia al pasarse: (x · d · 0.55) / (d + 0.55 · |x|)
const rubber = (x, d) => (x * d * RESISTANCE) / (d + RESISTANCE * Math.abs(x))

export default function useZoomPan({ containerRef, videoRef, wrapRef, enabled, reduceMotion, onTap, onDoubleTap }) {
  const state = useRef({ z: 1, x: 0, y: 0 })
  const pointers = useRef(new Map())
  const gesture = useRef(null)
  const raf = useRef(null)
  const lastTap = useRef(0)
  const tapTimer = useRef(null)

  const apply = useCallback((animate) => {
    const el = wrapRef.current
    if (!el) return
    const { z, x, y } = state.current
    el.style.transition = animate && !reduceMotion ? 'transform 0.4s cubic-bezier(0.2, 0.9, 0.3, 1)' : 'none'
    el.style.transform = `translate(${x}px, ${y}px) scale(${z})`
  }, [wrapRef, reduceMotion])

  // Tamaño del video tal como se ve con contain (sin zoom) y zoom que lo llena.
  const metrics = useCallback(() => {
    const c = containerRef.current
    const v = videoRef.current
    if (!c) return null
    const cw = c.clientWidth
    const ch = c.clientHeight
    const vw = v?.videoWidth || cw
    const vh = v?.videoHeight || ch
    const contain = Math.min(cw / vw, ch / vh)
    const cover = Math.max(cw / vw, ch / vh)
    const rw = vw * contain
    const rh = vh * contain
    const fill = cover / contain
    return { cw, ch, rw, rh, fill, maxZoom: Math.max(MIN_MAX_ZOOM, fill * 2) }
  }, [containerRef, videoRef])

  // Límite del desplazamiento para un zoom dado: sin bordes negros donde había imagen.
  const bounds = (m, z) => ({
    bx: Math.max(0, (m.rw * z - m.cw) / 2),
    by: Math.max(0, (m.rh * z - m.ch) / 2),
  })

  const clamp = (v, lim) => Math.min(lim, Math.max(-lim, v))

  const stopInertia = () => { if (raf.current) { cancelAnimationFrame(raf.current); raf.current = null } }

  const reset = useCallback((animate = false) => {
    stopInertia()
    state.current = { z: 1, x: 0, y: 0 }
    apply(animate)
  }, [apply])

  // Acomodo al soltar: dentro de los límites, con el snap a 1× / llenar.
  const settle = useCallback(() => {
    const m = metrics()
    if (!m) return
    let { z, x, y } = state.current
    if (z < SNAP_TO_FIT) { z = 1 }
    else if (Math.abs(z - m.fill) / m.fill < SNAP_TO_FILL) {
      z = m.fill
      try { navigator.vibrate?.(8) } catch {}
    }
    z = Math.min(Math.max(z, 1), m.maxZoom)
    const { bx, by } = bounds(m, z)
    state.current = { z, x: clamp(x, bx), y: clamp(y, by) }
    apply(true)
  }, [metrics, apply])

  const startInertia = (vx, vy) => {
    const m = metrics()
    if (!m || reduceMotion) { settle(); return }
    let last = performance.now()
    const step = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      const s = state.current
      const { bx, by } = bounds(m, s.z)
      s.x = clamp(s.x + vx * dt, bx)
      s.y = clamp(s.y + vy * dt, by)
      vx *= Math.pow(0.04, dt)
      vy *= Math.pow(0.04, dt)
      apply(false)
      if (Math.hypot(vx, vy) > 12) raf.current = requestAnimationFrame(step)
      else raf.current = null
    }
    raf.current = requestAnimationFrame(step)
  }

  const cbRef = useRef({})
  cbRef.current = { onTap, onDoubleTap }

  const onPointerDown = (e) => {
    if (!enabled) { pointers.current.clear(); gesture.current = null; return }
    stopInertia()
    // Un puntero que quedó «atascado» (soltado fuera, cancelado o con el hook
    // deshabilitado) haría creer que hay dos dedos y los toques dejarían de
    // mostrar los controles: con un dedo nuevo sin otros apoyados, se limpia.
    if (e.pointerType === 'touch' && e.isPrimary) pointers.current.clear()
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
    const c = containerRef.current.getBoundingClientRect()
    if (pointers.current.size === 1) {
      gesture.current = { type: 'pan', startX: e.clientX, startY: e.clientY, moved: false, vx: 0, vy: 0, t: performance.now() }
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      gesture.current = {
        type: 'pinch',
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        z0: state.current.z,
        moved: true,
        cx: (a.x + b.x) / 2 - c.left - c.width / 2,
        cy: (a.y + b.y) / 2 - c.top - c.height / 2,
        x0: state.current.x,
        y0: state.current.y,
      }
    }
  }

  const onPointerMove = (e) => {
    const g = gesture.current
    if (!enabled || !g || !pointers.current.has(e.pointerId)) return
    const prev = pointers.current.get(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const m = metrics()
    if (!m) return

    if (g.type === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1
      const c = containerRef.current.getBoundingClientRect()
      const fx = (a.x + b.x) / 2 - c.left - c.width / 2
      const fy = (a.y + b.y) / 2 - c.top - c.height / 2
      // Un poco por encima del máximo cede con resistencia y al soltar vuelve.
      let z = g.z0 * (dist / g.dist)
      if (z > m.maxZoom) z = m.maxZoom + rubber(z - m.maxZoom, m.maxZoom)
      if (z < 1) z = 1 - rubber(1 - z, 1)
      const k = z / g.z0
      // El punto bajo los dedos se queda bajo los dedos.
      const nx = fx - (g.cx - g.x0) * k
      const ny = fy - (g.cy - g.y0) * k
      state.current = { z, x: nx, y: ny }
      apply(false)
      return
    }

    if (g.type === 'pan') {
      const dx = e.clientX - prev.x
      const dy = e.clientY - prev.y
      if (!g.moved && Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < 6) return
      g.moved = true
      if (state.current.z <= 1.001) return // sin zoom no hay nada que recorrer
      const now = performance.now()
      const dt = Math.max(1, now - g.t) / 1000
      g.vx = 0.6 * g.vx + 0.4 * (dx / dt)
      g.vy = 0.6 * g.vy + 0.4 * (dy / dt)
      g.t = now
      const { bx, by } = bounds(m, state.current.z)
      let x = state.current.x + dx
      let y = state.current.y + dy
      if (Math.abs(x) > bx) x = Math.sign(x) * (bx + rubber(Math.abs(x) - bx, m.cw))
      if (Math.abs(y) > by) y = Math.sign(y) * (by + rubber(Math.abs(y) - by, m.ch))
      state.current = { ...state.current, x, y }
      apply(false)
    }
  }

  const onPointerUp = (e) => {
    const g = gesture.current
    pointers.current.delete(e.pointerId)
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch {}
    if (!enabled) { if (pointers.current.size === 0) gesture.current = null; return }

    if (pointers.current.size > 0) {
      // Quedó un dedo tras el pellizco: sigue como arrastre desde ahí.
      const [p] = [...pointers.current.values()]
      gesture.current = { type: 'pan', startX: p.x, startY: p.y, moved: true, vx: 0, vy: 0, t: performance.now() }
      return
    }
    gesture.current = null
    if (!g) return

    if (g.type === 'pinch') { settle(); return }

    if (!g.moved) {
      // Toque: uno solo muestra u oculta los controles, dos seguidos cambian de cámara.
      const now = Date.now()
      if (now - lastTap.current < 280) {
        lastTap.current = 0
        clearTimeout(tapTimer.current)
        cbRef.current.onDoubleTap?.()
      } else {
        lastTap.current = now
        clearTimeout(tapTimer.current)
        tapTimer.current = setTimeout(() => { lastTap.current = 0; cbRef.current.onTap?.() }, 280)
      }
      return
    }

    if (state.current.z > 1.001) {
      const m = metrics()
      const { bx, by } = m ? bounds(m, state.current.z) : { bx: 0, by: 0 }
      const outside = Math.abs(state.current.x) > bx + 0.5 || Math.abs(state.current.y) > by + 0.5
      if (outside) settle()
      else startInertia(g.vx, g.vy)
    }
  }

  // Sin zoom el cuadro vuelve a entrar completo si cambia el tamaño de la ventana.
  useEffect(() => {
    const onResize = () => { if (state.current.z > 1) settle() }
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('resize', onResize); stopInertia(); clearTimeout(tapTimer.current) }
  }, [settle])

  useEffect(() => { if (!enabled) reset(false) }, [enabled, reset])

  return { handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onLostPointerCapture: onPointerUp }, reset }
}
